import React, { useState } from "react";
import { useTheme, Box } from "@mui/material";
import WidgetAlert, { WidgetErrorType } from "components/widgetParts/WidgetAlert";
import withQueryWidget, { WithQueryWidgetProps } from "components/widgetParts/queryWidgetComposition/withQueryWidget";
import { socialDatasetId } from "system/query/Query";
import useAuth from "system/APIAuth";
import APIRequest from "system/APIRequest";
import { getSingleWidgetResponseByName } from "system/widget/Widget";
import { useSnackbar } from "notistack";
import { useTranslation } from 'react-i18next'
import { Post, PostListRequestBody, PostListResponse, postListDefaultSettings } from "./PostListUtils";
import PostListContent from "./PostListContent";

function PostListWidget(props: WithQueryWidgetProps): React.JSX.Element {
  const auth = useAuth();
  const { i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();

  const currentState = { ...postListDefaultSettings, ...props.widget?.data?.settings};
  const apiResponseInfo = getSingleWidgetResponseByName(props.apiResponses, 'post-list');
  const apiResponse:PostListResponse | undefined = apiResponseInfo?.responseBody;
  const isInvalidDataset = !apiResponse && props?.queryInfo?.dataset?.id !== socialDatasetId;

  const [loadedApiResponse, setLoadedApiResponse] = useState<PostListResponse | undefined>(apiResponse);
  const [records, setRecords] = useState<Post[]>(apiResponse?.records || []);
  const [cursor, setCursor] = useState<string | null>(apiResponse?.cursor || null);
  const hasRecords = records.length > 0;

  // Reset paginated records/cursor whenever a new API response comes in (new query, refresh, etc.)
  if (apiResponse !== loadedApiResponse) {
    setLoadedApiResponse(apiResponse);
    setRecords(apiResponse?.records || []);
    setCursor(apiResponse?.cursor || null);
  }

  // Fetch more posts by updating the request body with the cursor
  const [isFetchingMore, setIsFetchingMore] = useState<boolean>(false);
  const fetchMorePosts = () => {
    const requestInfo:PostListRequestBody = apiResponseInfo?.originatingRequest?.requestInit?.body;
    if (!requestInfo?.records || cursor === null || isFetchingMore) return;

    const newRequestInfo:PostListRequestBody = {
      ...requestInfo,
      records: {
        ...requestInfo.records,
        cursor
      }
    };

    setIsFetchingMore(true);
    APIRequest(`query/records/${apiResponseInfo?.originatingRequest?.activeQueryIds?.join(",") || ""}`,  {}, {
      method: "POST",
      body: JSON.stringify(newRequestInfo)
    }).then((response) => {
      if (response) {
        const nextResponse:PostListResponse = response;
        setRecords((currentRecords) => [...currentRecords, ...(nextResponse.records || [])]);
        setCursor(nextResponse.cursor || null);
      }
    }).catch((error) => {
      enqueueSnackbar(i18n.t("widget-types.post-list.alerts.fetch-error", {errorMessage: error}), { variant: "error" });
    }).finally(() => {
      setIsFetchingMore(false);
    });
  };

  return (
    <Box sx={{ height: "100%", width: "100%", backgroundColor: theme.palette.background.default }}>
      {isInvalidDataset &&
        <WidgetAlert type={WidgetErrorType.INVALID_DATASET}>
          {i18n.t("widget-types.post-list.social-dataset-required")}
        </WidgetAlert>
      } 
      {!isInvalidDataset && hasRecords &&
        <PostListContent
          posts={records}
          labels={apiResponse?.value_labels || {}}
          cursor={cursor}
          pageSize={currentState.pageSize}
          isFetchingMore={isFetchingMore}
          onFetchMore={auth.user ? fetchMorePosts : undefined}
        />
      }
      {!isInvalidDataset && !hasRecords &&
        <WidgetAlert type={WidgetErrorType.NO_DATA} />
      }
    </Box>
  );
}

export default withQueryWidget(PostListWidget, {
  hasSettingsDialog: true,
  hasQueryDialog: true,
  exportOptions: ["50", "100", "250", "500", "1000", "2500", "5000", "10000"]
});
